import React, { ReactElement } from 'react';
import classNames from 'classnames/bind';
import Link from 'next/link';
import useWindowSize from 'utils/hooks/useWindowSize';
import styles from 'sections/DigitalTools/LogisticsExplorer/components/SectionFeatures/SectionFeatures.module.scss';

const cn = classNames.bind(styles);

const SectionFeatures = (): ReactElement => {
  const { width: screenWidth } = useWindowSize();

  return (
    <section className={cn('features')}>
      <div className={cn('container')}>

        <div className={cn('features__section', 'small')}>
          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Individual Quotation Flow
            </h2>
            <p className={cn('features__paragraph')}>
              Equip your customers with a smart way to calculate shipping costs in seconds.
              With the Logistics Freight Calculator, each client can create customised
              quotes and complete bookings directly through your platform.
            </p>
          </div>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/booking-mobile.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/booking-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/booking-tablet.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/booking-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/booking-desktop.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/booking-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/LogisticsExplorer/booking-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>
        </div>

        <div className={cn('features__section', 'small', 'ordered')}>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/rates-mobile.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/rates-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/rates-tablet.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/rates-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/rates-desktop.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/rates-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/LogisticsExplorer/rates-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>

          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Rate Management System
            </h2>
            <p className={cn('features__paragraph')}>
              Manage and modify your freight margins with ease. Our robust Rate Management
              System (RMS) supports multiple shipping modes, making it simple to reach global
              markets and promote your rates through SeaRates and DF Alliance.
            </p>
          </div>
        </div>

        <div className={cn('features__section', 'small')}>
          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Widget Customisation
            </h2>
            <p className={cn('features__paragraph')}>
              With complete control over design, you can customise the look and feel
              of your logistics calculator to match your brand.
            </p>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-request')}
                href="https://www.searates.com/tools#search-filter"
              >
                See how it works
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1 5h7" />
                  <path d="M2 1l4 4-4 4" />
                </svg>
              </Link>
            </div>
          </div>

          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/widget-mobile.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/widget-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/widget-tablet.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/widget-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/widget-desktop.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/widget-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/LogisticsExplorer/widget-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>
        </div>

        <div className={cn('features__section')}>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/localization-mobile.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/localization-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/localization-tablet.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/localization-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/localization-desktop.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/localization-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/LogisticsExplorer/localization-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>

          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              Language Localisation for Logistics Explorer
            </h2>
            <p className={cn('features__paragraph')}>
              Connect with a global customer base using a calculator that communicates
              clearly in their chosen language. Our logistics calculator supports customised,
              multilingual configurations, creating a personalised platform experience.
            </p>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-request')}
                href="https://www.searates.com/services/request-it-quote"
              >
                Request Your Quote Now
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1 5h7" />
                  <path d="M2 1l4 4-4 4" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className={cn('features__section')}>
          <div className={cn('features__image-box')}>
            <picture>
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/leads-mobile.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/leads-mobile@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 575px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/leads-tablet.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/leads-tablet@2x.webp 2x
              "
                sizes="100vw"
                media="(max-width: 1024px)"
              />
              <source
                srcSet="
                /images/pages/DigitalTools/LogisticsExplorer/leads-desktop.webp 1x,
                /images/pages/DigitalTools/LogisticsExplorer/leads-desktop@2x.webp 2x
              "
                sizes="100vw"
              />
              <img
                className={cn('features__image')}
                src="/images/pages/DigitalTools/LogisticsExplorer/leads-desktop.webp"
                alt="ship schedules"
              />
            </picture>
          </div>

          <div className={cn('features__content')}>
            <h2 className={cn('features__heading')}>
              New Way of Lead Generation
            </h2>
            <p className={cn('features__paragraph')}>
              DF Alliance’s Logistics Explorer is a conversion catalyst. Capture customer
              clicks, compare FCL and LCL sea freight costs, and turn transport time
              insights and transit tracking into trusted, targeted leads.
            </p>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-request')}
                href="https://www.searates.com/logistics-map/"
              >
                Learn More
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1 5h7" />
                  <path d="M2 1l4 4-4 4" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

        <div className={cn('features__section', 'no-image')}>
          <div className={cn('features__content', 'no-image')}>
            <h2 className={cn('features__heading', 'no-image')}>
              {screenWidth > 1025 &&
                <>
                  <span>Apply for Advanced</span>{' '}
                  <span>Freight Cost Estimator</span>{' '}
                  <span>Functionality</span>
                </>
              }
              {screenWidth <= 1025 &&
                <>
                  <span>Apply for Advanced</span>{' '}
                  <span>Freight Cost Estimator Functionality</span>
                </>
              }
            </h2>
          </div>

          <div className={cn('features__content')}>
            <p className={cn('features__paragraph')}>
              Strengthen the impact of your freight rates by sharing your tariffs with
              other DF Alliance members. This feature, helps you:
            </p>
            <ul className={cn('features__list')}>
              <li className={cn('features__list-item')}>Subscribe to partner-shared pricing for a stronger, more diverse service offering</li>
              <li className={cn('features__list-item')}>Showcase your shipping rates under partners’ local branding</li>
              <li className={cn('features__list-item')}>Book more business with zero-commission conversions across the DF Alliance network</li>
            </ul>
            <div className={cn('features__buttons')}>
              <Link
                className={cn('features__button-request')}
                href="/company/contact-us"
              >
                Contact us at DF Alliance to get started
                <svg viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M1 5h7" />
                  <path d="M2 1l4 4-4 4" />
                </svg>
              </Link>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default SectionFeatures;